import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation, faSpinner, faEnvelope, faUser } from "@abodeminehq/parcel-ui/icons/fa/pro-solid";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Alert,
  AlertDescription,
  AlertTitle,
  HStack,
  Input,
  Label,
  ZStack,
} from "@abodeminehq/parcel-ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  useOrganizationInvitationPublic,
  useAcceptOrganizationInvitation,
  useUpdateMyUserProfile,
} from "~/lib/queries/users";
import { useAuthContext } from "~/contexts/auth-context";
import { FirstScreen, Prompt } from "@logto/js";
import { AnimatedBackground } from "~/components/brand/animated-background";
import { rolesForPeopleUiDisplay } from "~/components/app/shared/role-selector";
import type { OrganizationRole } from "~/lib/schemas";

export const Route = createFileRoute("/invitation/accept/$invitationId")({
  component:  () => {return <ZStack> 
  <div className="w-screen h-screen pointer-events-none z-0">
  <AnimatedBackground />
  </div>
  <div className="pointer-events-auto z-1">
    <InvitationAcceptPage />
    </div>
    </ZStack>},
});

// Helper function to check if user has name information
function checkUserHasName(user: any): boolean {
  // Check if user has a general name field that's not empty
  if (user.name && user.name.trim().length > 0) {
    return true;
  }

  // Check if user has both first and last name in profile
  if (
    user.profile?.givenName &&
    user.profile?.familyName &&
    user.profile.givenName.trim().length > 0 &&
    user.profile.familyName.trim().length > 0
  ) {
    return true;
  }

  return false;
}

function InvitationAcceptPage() {
  const auth = useAuthContext();
  const navigate = useNavigate();
  const { invitationId } = Route.useParams();

  const [status, setStatus] = useState<
    | "ready"
    | "name_required"
    | "redirecting"
    | "error"
    | "expired"
    | "already_processed"
  >("ready");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [acceptedOrgId, setAcceptedOrgId] = useState<string>("");
  const postAcceptRedirectStarted = useRef(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [nameError, setNameError] = useState("");

  const isTerminalStatus =
    status === "redirecting" ||
    status === "expired" ||
    status === "already_processed" ||
    status === "error";

  // Query hooks — disabled once a terminal state is reached so a background
  // refetch can never overwrite the current status (e.g. "success" → "already_processed")
  const invitationQuery = useOrganizationInvitationPublic(invitationId, {
    enabled: !isTerminalStatus,
  });

  // Mutation hooks
  const acceptInvitationMutation = useAcceptOrganizationInvitation();
  const updateProfileMutation = useUpdateMyUserProfile();

  useLayoutEffect(() => {
    if (
      status !== "redirecting" ||
      !acceptedOrgId ||
      postAcceptRedirectStarted.current
    ) {
      return;
    }
    postAcceptRedirectStarted.current = true;

    if (typeof window !== "undefined" && window.sessionStorage) {
      sessionStorage.setItem(
        "post_signin_redirect_uri",
        new URL(`/org/${acceptedOrgId}`, window.location.origin).href,
      );
    }

    auth.logtoClient.signIn({
      redirectUri: new URL("/callback", window.location.origin).href,
      prompt: Prompt.Consent,
      loginHint: auth.user?.email ?? undefined,
    });
  }, [status, acceptedOrgId, auth.logtoClient, auth.user?.email]);

  // Handle invitation query results and user state
  useEffect(() => {
    // Never overwrite a terminal state — the query is disabled at this point
    // anyway, but this guards against any edge-case race conditions.
    if (isTerminalStatus) return;

    if (!invitationId) {
      setStatus("error");
      setErrorMessage("Invalid invitation link");
      return;
    }

    if (invitationQuery.error) {
      const error = invitationQuery.error;
      if (error instanceof Error) {
        switch (error.message) {
          case "EXPIRED":
            setStatus("expired");
            break;
          case "ALREADY_PROCESSED":
            setStatus("already_processed");
            break;
          default:
            setStatus("error");
            setErrorMessage(
              "This invitation link is invalid or no longer exists",
            );
        }
      } else {
        setStatus("error");
        setErrorMessage("Failed to load invitation details");
      }
      return;
    }

    if (
      invitationQuery.data &&
      auth.user &&
      auth.user.email === invitationQuery.data.email
    ) {
      const hasName = checkUserHasName(auth.user);
      if (!hasName) {
        setStatus("name_required");
      } else {
        setStatus("ready");
      }
    } else if (invitationQuery.data) {
      setStatus("ready");
    }
  }, [invitationId, invitationQuery.data, invitationQuery.error, auth.user, isTerminalStatus]);

  const handleAcceptInvitation = () => {
    if (!invitationId) return;

    acceptInvitationMutation.mutate(
      { invitation_id: invitationId },
      {
        onSuccess: async (result) => {
          if (result.success) {
            setAcceptedOrgId(result.organizationId);
            setStatus("redirecting");
          }
        },
        onError: (error) => {
          console.error("Failed to accept invitation:", error);

          if (error instanceof Error) {
            switch (error.message) {
              case "EXPIRED":
                setStatus("expired");
                break;
              case "ALREADY_PROCESSED":
                setStatus("already_processed");
                break;
              default:
                setStatus("error");
                setErrorMessage(
                  "Failed to accept invitation. Please try again.",
                );
            }
          } else {
            setStatus("error");
            setErrorMessage("An unexpected error occurred");
          }
        },
      },
    );
  };

  const handleAuthIn = (firstScreen: FirstScreen) => {
    //set redirect uri to the current page
    const redirectUri = new URL(window.location.href).toString();
    //save to session storage
    if (typeof window !== "undefined" && window.sessionStorage) {
      sessionStorage.setItem("post_signin_redirect_uri", redirectUri);
    }

    auth.logtoClient.signIn({
      redirectUri: new URL("/callback", window.location.origin).href,
      firstScreen: firstScreen,
      loginHint: invitationQuery.data?.email,
      prompt: [Prompt.Login, Prompt.Consent],
    });
  };

  const handleNameSubmit = () => {
    // Basic validation
    if (!firstName.trim() || !lastName.trim()) {
      setNameError("Both first and last name are required");
      return;
    }

    setNameError("");

    updateProfileMutation.mutate(
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      },
      {
        onSuccess: (result) => {
          if (result.success) {
            // Name updated successfully, now check if we can proceed to ready state
            setStatus("ready");
          }
        },
        onError: (error) => {
          console.error("Failed to update name:", error);
          setNameError("Failed to update name. Please try again.");
        },
      },
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Organization Invitation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {invitationQuery.isLoading && (
            <div className="text-center space-y-4">
              <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 animate-spin mx-auto text-blue-600" />
              <p className="text-gray-600">Loading invitation details...</p>
            </div>
          )}

          {status === "ready" && invitationQuery.data && (
            <div className="text-center space-y-4">
              <FontAwesomeIcon icon={faEnvelope} className="h-8 w-8 mx-auto text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold">
                  Join{" "}
                  {invitationQuery.data.organization?.name ??
                    "this Organization"}
                </h3>
                <p className="text-muted-foreground mt-2">
                  You've been invited to join{" "}
                  {invitationQuery.data.organization?.name ??
                    "this Organization"}{" "}
                  with the following roles:
                </p>
                <div className="mt-3 mb-4">
                  {rolesForPeopleUiDisplay(
                    invitationQuery.data.roles as OrganizationRole[],
                  ).map((role) => (
                    <span
                      key={role}
                      className="inline-block bg-red-100 text-red-800 text-sm px-3 py-1 rounded-full mr-2 mb-1"
                    >
                      {role}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  Invited: {invitationQuery.data.email}
                </p>
              </div>
              {/* 3 states.  1. user logged into correct account. 2. user logged into wrong account. 3. user not logged in. */}
              {auth.user != null &&
                auth.user?.email === invitationQuery.data.email && (
                  <>
                    {checkUserHasName(auth.user) ? (
                      <Button
                        onClick={handleAcceptInvitation}
                        className="w-full"
                        disabled={acceptInvitationMutation.isPending}
                      >
                        {acceptInvitationMutation.isPending
                          ? "Accepting..."
                          : "Accept Invitation"}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setStatus("name_required")}
                        className="w-full"
                      >
                        Complete Profile & Accept Invitation
                      </Button>
                    )}
                  </>
                )}
              {auth.user != null &&
                auth.user?.email !== invitationQuery.data.email && (
                  <>
                    <Alert variant="warning">
                      <AlertTitle>
                        <HStack spacing={2}>
                          <FontAwesomeIcon icon={faTriangleExclamation} />
                          Wrong Account
                        </HStack>
                      </AlertTitle>
                      <AlertDescription>
                        You are logged in as <code>{auth.user?.email}</code>.
                        You need to switch to the correct account to accept the
                        invitation.
                      </AlertDescription>
                    </Alert>
                    <Button
                      onClick={() => handleAuthIn("sign_in")}
                      className="w-full"
                    >
                      Switch Account
                    </Button>
                  </>
                )}
              {!auth.user && (
                <>
                  <Button
                    onClick={() => handleAuthIn("register")}
                    className="w-full"
                  >
                    Create Account
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleAuthIn("sign_in")}
                    className="w-full"
                  >
                    Sign In
                  </Button>
                </>
              )}
            </div>
          )}

          {status === "name_required" && (
            <div className="text-center space-y-4">
              <FontAwesomeIcon icon={faUser} className="h-8 w-8 mx-auto text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold">Complete Your Profile</h3>
                <p className="text-muted-foreground mt-2">
                  Please provide your first and last name to continue with the
                  invitation.
                </p>
              </div>

              {nameError && (
                <Alert variant="destructive">
                  <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />
                  <AlertDescription>{nameError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-4 text-left">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <Button
                onClick={handleNameSubmit}
                className="w-full"
                disabled={updateProfileMutation.isPending}
              >
                {updateProfileMutation.isPending ? "Updating..." : "Continue"}
              </Button>
            </div>
          )}

          {updateProfileMutation.isPending && (
            <div className="text-center space-y-4">
              <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 animate-spin mx-auto text-blue-600" />
              <p className="text-gray-600">Updating your profile...</p>
            </div>
          )}

          {acceptInvitationMutation.isPending && (
            <div className="text-center space-y-4">
              <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 animate-spin mx-auto text-accent-600" />
              <p className="text-gray-600">Accepting invitation...</p>
            </div>
          )}

          {status === "redirecting" && (
            <div className="text-center space-y-4">
              <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 animate-spin mx-auto text-accent-600" />
              <p className="text-gray-600">Taking you to your organization…</p>
            </div>
          )}

          {status === "expired" && (
            <div className="text-center space-y-4">
              <FontAwesomeIcon icon={faTriangleExclamation} className="h-8 w-8 mx-auto text-yellow-600" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-800">
                  Invitation Expired
                </h3>
                <p className="text-gray-600 mt-2">
                  This invitation link has expired. Please contact the
                  organization administrator for a new invitation.
                </p>
              </div>
              <Button onClick={() => navigate({ to: "/" })}>
                Go to Homepage
              </Button>
            </div>
          )}

          {status === "already_processed" && (
            <div className="text-center space-y-4">
              <FontAwesomeIcon icon={faTriangleExclamation} className="h-8 w-8 mx-auto text-orange-600" />
              <div>
                <h3 className="text-lg font-semibold text-orange-800">
                  Invitation Already Used
                </h3>
                <p className="text-gray-600 mt-2">
                  This invitation has already been accepted or has been revoked.
                </p>
              </div>
              <Button onClick={() => navigate({ to: "/" })}>
                Go to Homepage
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="text-center space-y-4">
              <FontAwesomeIcon icon={faTriangleExclamation} className="h-8 w-8 mx-auto text-red-600" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">
                  Invalid Invitation
                </h3>
                <p className="text-gray-600 mt-2">
                  {errorMessage ||
                    "This invitation link is invalid. Please contact the organization administrator."}
                </p>
              </div>
              <Button onClick={() => navigate({ to: "/" })}>
                Go to Homepage
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
